import React, { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import Heading from '@atlaskit/heading';

import AccountVerificationForm from '../components/AccountVerificationForm';
import { TsvRecoveryFieldError } from '../types';
import { RecoveryPageMessages } from './RecoveryPage.messages';
import emailContainerMessages from '../components/EmailContainer.messages';
import WhiteboxPage from '../components/WhiteboxPage';
import { loginRecovery, reset, showFieldErrors } from '../actions/recovery-actions';
import {
  useAnalyticsClient,
  useButtonClickedEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';

const FormWrapper = styled.div`
  margin-top: ${gridSize()}px;
`;

const pageId = '2svRecoveryLoginScreen';

export async function recoverLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/2step/recover/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa-recover'],
  });
}

export const RecoveryPage = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();
  const navigate = useNavigateSoftOrHard();

  usePageViewedEvent(pageId);

  const { error, fieldErrors, redirectTo, isLoading } = useAppSelector(state => state.recovery);
  const email = useAppSelector(state => state.user.email);

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  const handleValidationError = useCallback(
    (errors: TsvRecoveryFieldError) => {
      dispatch(showFieldErrors(errors));
    },
    [dispatch]
  );

  const handleLogIn = useCallback(
    (username: string, password: string) => {
      dispatch(
        loginRecovery({
          username,
          password,
          location: window.location,
          pageId: pageId,
          analyticsClient: analyticsClient,
        })
      );
    },
    [dispatch, analyticsClient]
  );

  const buildHeader = () => {
    return (
      <div>
        <Heading size="small" as="h1">
          <FormattedMessage id="recovery.title" defaultMessage="Login to your Atlassian account" />
        </Heading>
      </div>
    );
  };

  const onTsvLoginClick = useButtonClickedEvent(pageId, '2svRecoveryLogin');

  return (
    <WhiteboxPage pageId={pageId} header={buildHeader()}>
      <Helmet title={formatMessage(RecoveryPageMessages.headTitle)} />
      <FormWrapper>
        <AccountVerificationForm
          email={email}
          emailFieldLabel={formatMessage(emailContainerMessages.loginFormUsernameLabel)}
          error={error}
          fieldErrors={fieldErrors}
          isLoading={isLoading}
          onValidationError={handleValidationError}
          submitButtonText={<FormattedMessage id="common.login" defaultMessage="Log in" />}
          onSubmitClick={onTsvLoginClick}
          onSubmit={handleLogIn}
        />
      </FormWrapper>
    </WhiteboxPage>
  );
};

export default RecoveryPage;
